import React, { Fragment, FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { closeModal } from '../../store/modules/ModalActions';
import { getElementID } from '../../utils/HTMLUtils';
import { Col } from '../Col';
import { CustomButton } from '../CustomButton';
import { GenericModal } from '../GenericModal';
import CheckIcon from '../Icons/CheckIcon';
import { Row } from '../Row';
import styles from './ConfirmModal.scss';

const rootClassName = styles.rootClassName;
const rootElementId = 'confirm-modal';

interface ConfirmModalProps {
  title: string;
  content: string | React.ReactNode;
  confirmCallback: () => void;
  cancelCallback?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  modalKey?: string;
}

const ConfirmModal: FC<ConfirmModalProps> = ({
  title,
  content,
  confirmCallback,
  cancelCallback,
  confirmLabel,
  cancelLabel,
  modalKey,
}) => {
  const dispatch = useDispatch();

  const languageValues = useSelector(
    (state: RootState) => state.languages.languageValues
  );

  const toClose = useCallback(() => {
    dispatch(closeModal(modalKey));
  }, [dispatch, modalKey]);

  const toCancel = useCallback(() => {
    if (typeof cancelCallback === 'function') {
      cancelCallback();
    }
    toClose();
  }, [cancelCallback, toClose]);

  const toConfirm = useCallback(() => {
    confirmCallback();
    toClose();
  }, [confirmCallback, toClose]);

  return (
    <GenericModal
      id={rootElementId}
      onClose={toCancel}
      DialogProps={{
        maxWidth: 'sm',
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
      }}
      title={title}
      footerContent={
        <Fragment>
          <CustomButton
            id={getElementID(rootElementId, 'cancel')}
            theme="pufvYellow"
            className={`${rootClassName}-cancel-button`}
            label={cancelLabel || languageValues.cancelButton}
            onClick={toCancel}
          />
          <CustomButton
            id={getElementID(rootElementId, 'confirm')}
            theme="sicrediGreen"
            className={`${rootClassName}-confirm-buttom`}
            label={confirmLabel || languageValues.confirmButton}
            rightIcon={<CheckIcon />}
            onClick={toConfirm}
          />
        </Fragment>
      }
    >
      <Row>
        <Col cols={[12, 12, 12, 12, 12]}>{content}</Col>
      </Row>
    </GenericModal>
  );
};

export default ConfirmModal;